import React, { useState, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { userAtom, showCartAtom, cartListAtom } from '../../store';
import { useAuth } from '../../context/authContext';
import { createCart } from '../../firebase/carts';
import { createCheckouts } from '../../firebase/payments';
import { getAuthHeaders } from '../../firebase/authHeaders';
import DialogLoading from '../dialogs/DialogLoading';
import CartItem from '../cards/CartItem';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const SidebarCart = () => {
    const navigate = useNavigate();
    const [userData, setUserData] = useAtom(userAtom);
    const [showCart, setShowCart] = useAtom(showCartAtom);
    const [cartList, setCartList] = useAtom(cartListAtom);
    const [loading, setLoading] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('Card'); // Default to Card

    // All available payment methods
    const allPaymentMethods = [
        { id: 'Card', name: 'Credit/Debit Card', icon: '💳' },
        { id: 'PayPal', name: 'PayPal', icon: '🔵' },
        { id: 'ACH Bank Debit', name: 'ACH Bank Debit', icon: '🏦' },
        { id: 'Bank Wire', name: 'Bank Wire', icon: '💸' }
    ];

    // Check if user can use a specific payment method
    const canUsePaymentMethod = (methodId) => {
        console.log('🔍 Checking payment method availability:', methodId, 'for user:', userData?.id, 'userData:', userData?.payment);
        switch (methodId) {
            case 'Card':
                return true; // Always available
            case 'PayPal':
                return true; // Always available
            case 'ACH Bank Debit':
                return true; // Always available
            case 'Bank Wire':
                // Bank Wire requires customerId to be set up
                // return !!userData.customer_id;
                return true;
            default:
                return true;
        }
    };

    // Get available payment methods considering user setup
    const getAvailablePaymentMethods = () => {
        if (cartList.length === 0) return allPaymentMethods;
        
        // Get all unique payment methods from all cards in cart
        const cardPaymentMethods = new Set();
        cartList.forEach(item => {
            if (item.paymentMethods && Array.isArray(item.paymentMethods)) {
                item.paymentMethods.forEach(method => cardPaymentMethods.add(method));
            }
        });
        
        console.log('🔍 Payment method analysis:', {
            cartListLength: cartList.length,
            cardPaymentMethods: Array.from(cardPaymentMethods),
            userHasCustomerId: !!userData?.customer_id
        });
        
        // If no cards have payment methods specified, show all methods
        if (cardPaymentMethods.size === 0) return allPaymentMethods;
        
        // Filter payment methods to only show those supported by cards AND user can use
        const availableMethods = allPaymentMethods.filter(method => 
            cardPaymentMethods.has(method.id) && canUsePaymentMethod(method.id)
        );
        
        console.log('✅ Available payment methods:', availableMethods.map(m => m.id));
        
        // If no methods match, fallback to basic methods that don't require setup
        if (availableMethods.length === 0) {
            const fallbackMethods = allPaymentMethods.filter(method => canUsePaymentMethod(method.id));
            console.log('🔄 Using fallback methods:', fallbackMethods.map(m => m.id));
            return fallbackMethods;
        }
        
        return availableMethods;
    };

    // Get available payment methods
    const paymentMethods = getAvailablePaymentMethods();

    // Update selected payment method if current selection is not available
    useEffect(() => {
        const availableMethodIds = paymentMethods.map(method => method.id);
        if (!availableMethodIds.includes(selectedPaymentMethod)) {
            // Set to first available method
            setSelectedPaymentMethod(availableMethodIds[0] || 'Card');
        }
    }, [cartList, paymentMethods]);

    const normalizeCartItems = (items = []) => {
        const cartMap = new Map();

        (Array.isArray(items) ? items : []).forEach((item) => {
            if (!item) return;

            const key = item.id ? `id:${item.id}` : (item.shopify_variant_id || item.variant_id || item.variantId) ? `variant:${item.shopify_variant_id || item.variant_id || item.variantId}` : `title:${item.title || 'untitled'}:${item.price || 0}`;
            const current = cartMap.get(key);
            const count = Number(item.count || 1);

            if (current) {
                cartMap.set(key, {
                    ...current,
                    ...item,
                    count: Math.max(Number(current.count || 1), count),
                    price: Number(item.price || current.price || 0)
                });
            } else {
                cartMap.set(key, { ...item, count, price: Number(item.price || 0) });
            }
        });

        return Array.from(cartMap.values());
    };

    const normalizedCartList = useMemo(() => normalizeCartItems(cartList), [cartList]);

    const getTotalPrice = (items) => {
        return items.reduce((total, item) => total + (Number(item.price || 0) * Number(item.count || 1)), 0);
    };

    const totalPrice = getTotalPrice(normalizedCartList);

    const processingFee = useMemo(() => {
        // Example: 2.9% + $0.30 for card payments
        if (selectedPaymentMethod === 'Card') {
            const payPrice = (totalPrice + 0.3) / (1 - 0.029);
            return Math.ceil(payPrice*100)/100 - totalPrice;
        }
        else if(selectedPaymentMethod === 'ACH Bank Debit') {
            function achGrossAmount(net) {
                const rate = 0.008;
                const cap = 5;

                // Try uncapped formula
                const grossUncapped = net / (1 - rate);

                // If that gross would trigger the cap, use capped logic
                if (grossUncapped * rate >= cap) {
                    return Math.ceil((net + cap) * 100) / 100;
                }
                return Math.ceil(grossUncapped * 100) / 100;
            }
            const achPrice = achGrossAmount(totalPrice);
            return achPrice - totalPrice;
        }
        return 0;
    }, [selectedPaymentMethod, totalPrice]);

    const checkout = async () => {
        // An account is the one thing a buyer must have: no membership, but
        // also no guest checkout. Everything below reads userData.id, so this
        // has to come before the payment-method check rather than after it.
        if (!userData?.id) {
            setShowCart(false);
            navigate('/register');
            return;
        }

        // Validate payment method can be used
        if (!canUsePaymentMethod(selectedPaymentMethod)) {
            if (selectedPaymentMethod === 'Bank Wire') {
                alert('Bank Wire requires account setup. Please use a different payment method or contact support.');
            } else {
                alert('The selected payment method is not available. Please choose a different option.');
            }
            return;
        }

        setLoading(true)

        // Create cart items first
        const cartIdList = []
        cartList.forEach(async (cart) => {
            const cartId = await createCart(userData.id, cart)
            console.log("cart detail", cart)
            cartIdList.push(cartId)
        })

        console.log('=== CHECKOUT PROCESS STARTED ===');

        // The server looks up each item's real price in Firestore (listings /
        // shopify_products) instead of trusting a client-sent amount, so only
        // item references are sent -- for PayPal as well as for Stripe.
        //
        // Sale records and their buyer/seller notifications are written
        // server-side once payment completes. The page used to also write
        // them from localStorage on return, which duplicated every
        // notification and addressed Shopify ones to a literal 'shopify' id.
        const cartItemRefs = cartList.map((item) => ({
            id: item.id,
            quantity: item.count || 1,
            source: (item?.inventory_source === 'shopify' || item?.managed_by_shopify) ? 'shopify' : 'listing'
        }));

        const authHeaders = await getAuthHeaders();

        // Check if PayPal is selected
        const hasPayPal = selectedPaymentMethod === "PayPal";
        
        if (hasPayPal) {
            // Use PayPal direct checkout
            axios({
                method: 'post',
                url: process.env.REACT_APP_PAYMENT_API + '/create-paypal-checkout',
                headers: authHeaders,
                data: {
                    prevLink: window.location.href,
                    // PayPal must return the buyer to the route that handles
                    // the callback and captures the payment, not back to
                    // wherever the cart was opened from.
                    successLink: `${window.location.origin}/success`,
                    name: "CardMarketPlace Checkout for Cart",
                    cartItems: cartItemRefs
                }
            }).then(async ({ data }) => {
                if (data.success) {
                    await createCheckouts({
                        sessionId: data.paypalOrderId,
                        sessionLink: data.approvalUrl,
                        checkoutInfo: { type: 'cart', card_id_list: cartIdList, payment_method: 'paypal' }
                    });
                    
                    // Redirect to PayPal for payment
                    window.location.href = data.approvalUrl;
                    setLoading(false);
                } else {
                    throw new Error(data.error || 'PayPal checkout failed');
                }
            }).catch((error) => {
                console.error('PayPal checkout error:', error);
                setLoading(false);
                alert('PayPal checkout failed. Please try a different payment method.');
            });
        } else {
            // Use Stripe checkout for other payment methods
            const stripePaymentMethod = selectedPaymentMethod === "Card" ? "card" : 
                                     selectedPaymentMethod === "ACH Bank Debit" ? "us_bank_account" :
                                     selectedPaymentMethod === "Bank Wire" ? "customer_balance" : "card";

            axios({
                method: 'post',
                url: process.env.REACT_APP_PAYMENT_API + '/create-checkout',
                headers: authHeaders,
                data: {
                    customerId: userData.customer_id,
                    prevLink: window.location.href,
                    paymentMethodTypes: [stripePaymentMethod],
                    name: "CardMarketPlace Checkout for Cart",
                    cartItems: cartItemRefs
                }
            }).then(async ({ data }) => {
                console.log('✅ Stripe checkout successful:', data);
                await createCheckouts({
                    sessionId: data.session.id,
                    sessionLink: data.session.url,
                    checkoutInfo: { type: 'cart', card_id_list: cartIdList }
                })

                window.location.href = data.session.url;
                setLoading(false)
            }).catch((error) => {
                console.error('❌ Stripe checkout failed:', error);
                console.error('Request data sent:', {
                    amount: getTotalPrice(cartList),
                    customerId: userData?.customer_id,
                    paymentMethodTypes: [stripePaymentMethod],
                    selectedPaymentMethod
                });
                setLoading(false);
                
                // Handle specific payment method errors
                if (error.response?.data?.error) {
                    const errorMessage = error.response.data.error;
                    if (errorMessage.includes('Bank transfer')) {
                        alert('Bank transfer is not available. Please use Card payment instead.');
                    } else if (errorMessage.includes('No valid payment methods available')) {
                        // Handle the specific error we're seeing
                        if (selectedPaymentMethod === 'Bank Wire') {
                            alert('Bank Wire requires account setup. Please use Card payment instead or contact support to enable Bank Wire.');
                        } else {
                            alert('The selected payment method is not available. Please try a different payment method.');
                        }
                    } else {
                        alert('Checkout failed: ' + errorMessage);
                    }
                } else {
                    alert('Checkout failed: Please try again.');
                }
            })
        }
    }

    return (
        showCart &&
        <>
            {
                loading &&
                <DialogLoading />
            }

            <div className="flex flex-col fixed right-0 z-[9999] bg-[#181818] md:w-[544px] w-full h-full md:px-6 py-8 max-md:top-[69px] border-l-[1px] border-gray overflow-x-hidden overflow-y-hidden">
                <div className="flex justify-between shrink-0">
                    <div className="flex max-md:px-4">
                        <img src="/assets/icons/icon-logo.svg" alt="user" className="mr-3" />

                        <span className="uppercase text-white text-nowrap">Your Cart ({normalizedCartList.length})</span>
                    </div>

                    <button onClick={() => setShowCart(false)}>
                        <img src="/assets/icons/icon-close-white.svg" alt="close" />
                    </button>
                </div>

                <hr className="border-gray my-6 max-md:hidden shrink-0"></hr>

                {
                    normalizedCartList.length > 0 ? (
                        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden md:pr-1">
                            <div className="space-y-6 pb-6">
                                {normalizedCartList.map((item, index) =>
                                    <div key={`cart-item-${index}`}>
                                        <CartItem item={item} />

                                        <hr className="border-gray my-6 max-md:hidden"></hr>
                                    </div>
                                )}
                            </div>

                            <div className="text-[16px] mt-auto bg-[#111111] md:-mx-6 md:px-6 py-6 shrink-0">
                                <div className="flex justify-between">
                                    <span className="uppercase text-[#696969] font-bold">Subtotal</span>
                                    <span className="text-white">$ {totalPrice.toFixed(2)}</span>
                                </div>

                                {/* Payment Method Selection */}
                                <div className="mt-4">
                                    <h4 className="text-white text-sm font-medium mb-3">Payment Method</h4>
                                    {paymentMethods.length > 0 ? (
                                        <div className="space-y-2">
                                            {paymentMethods.map((method) => (
                                                <label key={method.id} className="flex items-center cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value={method.id}
                                                        checked={selectedPaymentMethod === method.id}
                                                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                                        className="mr-3"
                                                    />
                                                    <span className="text-white text-sm">
                                                        {method.icon} {method.name}
                                                    </span>
                                                </label>
                                            ))}
                                            
                                            {/* Show setup requirement message for Bank Wire */}
                                            {selectedPaymentMethod === 'Bank Wire' && !userData?.customer_id && (
                                                <div className="text-[#FFA500] text-xs mt-2 p-2 bg-[#FFA500]/10 border border-[#FFA500]/20 rounded">
                                                    ⚠️ Bank Wire requires account setup. Please contact support to enable this payment method.
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-[#717171] text-sm">
                                            No payment methods available for the selected cards.
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div className="text-white">
                                        processing fees may apply based on the selected payment method.
                                    </div>
                                    
                                    {!!selectedPaymentMethod && (
                                        <div className="text-[#FFA500] text-xs mt-2">
                                            Processing fee: ${processingFee.toFixed(2)}
                                        </div>
                                    )}
                                </div>

                                <button
                                    className="w-full mt-6 block text-center text-white bg-primary hover:bg-white hover:text-primary transition uppercase mb-auto py-3 text-[16px]"
                                    onClick={checkout}
                                    disabled={loading || paymentMethods.length === 0}
                                >
                                    {loading ? 'Processing...' : `Checkout with ${selectedPaymentMethod}`}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col flex-1 min-h-0">
                            <div className="uppercase text-center my-auto">
                                <h2 className="text-base font-bold text-white text-center">Your cart is empty</h2>
                                <span className="text-[#636363] text-xs">You don't have any items in your cart</span>
                            </div>

                            <Link
                                className="solo-button block text-center text-white bg-primary uppercase mb-auto py-3 text-[16px]"
                                to='/marketplace/categories'
                                onClick={() => setShowCart(false)}
                            >
                                Continue Shopping
                            </Link>
                        </div>
                    )
                }
            </div>

            <div className="fixed max-md:hidden inset-0 z-[9998] bg-[#000] backdrop-blur-sm bg-opacity-75 transition-opacity" onClick={() => setShowCart(false)}>

            </div>
        </>
    )
}

export default SidebarCart